import React from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ToggleButtonComponent from '@Components/Results/reactions/reaction-details/toggle-button.component';
import ReactionCIStageDetailsComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-ci-stage-details.component';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import ReactionStageDetailComponent from '@Components/Results/reactions/reaction-details/reaction-stage-detail.component';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ConditionsReferenceExperimentComponent from '@Components/Results/reactions/reaction-details/conditions-reference-experiment.component';
import ReactionAdditionalInfoComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-additional-info.component';
import { openMultiStepReactionDialog } from '@Pages/RetroSynthesis/MultiStepReactionDialog/actions';
import { Checkbox } from '@els/els-react--checkbox';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';

export default class ReactionDetailRowComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      experimentalProcedureExpanded: false,
      referencesForcedShow: false,
    };

    const { databaseId, detail } = this.props;

    this.isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
    this.additionalInfoVisible =
      props.allowAdditionalInfoShow && this.isCIDatabase && ReactReactionAdditionalInfoHelper.isAdditionalInfoVisible(detail);

    this.goToMultipleStepScheme = this.goToMultipleStepScheme.bind(this);
    this.toggleExperimentalProcedureExpanded = this.toggleExperimentalProcedureExpanded.bind(this);
    this.toggleReferencesForcedShow = this.toggleReferencesForcedShow.bind(this);
  }

  componentDidMount() {
    this.recalculateRenderedRowsNumber();
  }

  componentDidUpdate(prevProps, prevState) {
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    if (
      prevState.experimentalProcedureExpanded !== experimentalProcedureExpanded ||
      prevState.referencesForcedShow !== referencesForcedShow
    ) {
      this.recalculateRenderedRowsNumber();
    }
  }

  getYields() {
    const yields = get(this, 'props.detail.yields', null);
    return yields
      ? yields.map((yiel, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="yield-row" key={index}>
            {yields.length > 1 && <strong>{ReactReactionDetailsHelper.getLetterTitle(index)}&nbsp;</strong>}
            <InnerHTML tag="span" html={(yiel && yiel.yieldValue) || 'n/a'} />
          </div>
        ))
      : null;
  }

  getMultiStepDetails() {
    const multiStepDetails = get(this, 'props.detail.multiStepDetails', null);
    return multiStepDetails
      ? multiStepDetails.map((step, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <InnerHTML tag="div" key={index} className="steps-row" html={step} mode="escape" />
        ))
      : null;
  }

  getExampleLabelTitle() {
    const { detail } = this.props;
    const lbl = detail.exampleLabel;
    const ttl = detail.exampleTitle;
    return `${lbl || ''} ${ttl || ''}`.trim() || 'Experimental Procedure';
  }

  getTrackingProps() {
    const trackingProps = {};
    const { metadata, detail } = this.props;
    if (!metadata.oldTrackingDisabled) {
      trackingProps.prepareTrackingData = !isEmpty(detail.citations) ? detail.citations[0] : null;
      trackingProps.dataTrackingAction = 'experimental procedure';
    }
    return trackingProps;
  }

  // Calculates number of rendered <tr>s
  recalculateRenderedRowsNumber() {
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    const { onRenderedRowsNumberChange } = this.props;
    const renderedRows =
      1 + (experimentalProcedureExpanded ? 1 : 0) + (referencesForcedShow ? 2 : 0) + (this.additionalInfoVisible ? 1 : 0);
    onRenderedRowsNumberChange(renderedRows);
  }

  toggleExperimentalProcedureExpanded() {
    this.setState(
      (prevState) => {
        return {
          experimentalProcedureExpanded: !prevState.experimentalProcedureExpanded,
        };
      },
      () => {
        const { experimentalProcedureExpanded } = this.state;
        const { detail, onExperimentalProcedureOpen } = this.props;
        if (experimentalProcedureExpanded && !isEmpty(detail.citations)) {
          onExperimentalProcedureOpen(detail.citations[0]);
        }
      }
    );
  }

  toggleReferencesForcedShow() {
    this.setState((prevState) => {
      return {
        referencesForcedShow: !prevState.referencesForcedShow,
      };
    });
  }

  goToMultipleStepScheme() {
    const { detail, metadata } = this.props;

    if (!this.schemeLoading) {
      this.schemeLoading = true;
      store.dispatch(
        openMultiStepReactionDialog({
          databaseId: metadata && metadata.databaseId,
          scheme: detail && detail.scheme,
          onClose: () => {
            this.schemeLoading = false;
          },
        })
      );
    }
  }

  render() {
    const {
      isHit,
      metadata,
      detail,
      index,
      largestProduct,
      onFullTextLinkClick,
      onIssueCitedCounterClick,
      onAbstractOpen,
      onAuthorClick,
      databaseId,
      reactionId,
      renderCheckboxesColumn,
      isChecked,
      isVisible,
      setChecked,
    } = this.props;
    const { experimentalProcedureExpanded, referencesForcedShow } = this.state;
    const mainRowClasses = classnames([
      'rx-reactions-table__item',
      'rx-table__group',
      'display-table-row',
      isHit ? 'rx-table__group--highlight' : null,
      experimentalProcedureExpanded ? 'no-border-bottom rx-reactions-table__row-expanded' : null,
      isVisible ? null : 'rx-reactions-table__row-not-visible',
    ]);

    return (
      <>
        <tr className={mainRowClasses} {...(!referencesForcedShow && CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem)}>
          {/* Conditions section */}
          {renderCheckboxesColumn && (
            <td className="rx-reactions-table__checkbox">
              <Checkbox
                id={`checkbox-${reactionId}_${index}`}
                className="u-els-margin-left u-els-margin-right-1o2"
                label={`Select ${index}`}
                shouldHideLabel
                checked={isChecked}
                onChange={() => setChecked(detail.id, !isChecked)}
              />
            </td>
          )}
          <td className="rx-reactions-table__conditions display-table-cell">
            <div className="rx-reactions-table__conditions__steps">
              {detail.multiStepDetails ? (
                <>
                  <div className="steps-row">
                    <InnerHTML tag="b" html={detail.classification[0]} extendedTags />
                    <b> with </b>
                    <InnerHTML tag="b" html={detail.stepsCount} extendedTags />
                    <b> steps </b>
                  </div>
                  {this.getMultiStepDetails()}
                  {detail.scheme && (
                    <button
                      type="button"
                      className="els-btn els-btn-xs els-btn-link blue e2e-view-scheme"
                      onClick={this.goToMultipleStepScheme}>
                      <span>View Scheme</span>
                      <svg className="els-btn-icon els-btn-icon-tiny">
                        <use xlinkHref="#icon-navigate-right" />
                      </svg>
                    </button>
                  )}
                </>
              ) : (
                <>
                  {detail.stages &&
                    detail.stages.map((stage, stageIndex, stages) => (
                      /* eslint-disable react/no-array-index-key */
                      <ReactionStageDetailComponent
                        metadata={metadata}
                        key={stageIndex}
                        index={index}
                        stage={stage}
                        stageIndex={stageIndex}
                        isCIDatabase={this.isCIDatabase}
                        hasMultipleStages={stages.length > 1}
                      />
                      /* eslint-enable react/no-array-index-key */
                    ))}

                  {this.isCIDatabase && <ReactionCIStageDetailsComponent largestProduct={largestProduct} detail={detail} />}
                </>
              )}
            </div>

            {/* Toggle buttons (Experimental Procedure and Reference) */}
            <div className="els-btn-group-left">
              {detail.experimentalProcedure && (
                <ToggleButtonComponent
                  className="rx-reactions-table__content-toggle els-btn-xs e2e-experimental-procedure"
                  ariaControls={`reaction-procedure-${index}`}
                  expanded={experimentalProcedureExpanded}
                  toggleExpand={this.toggleExperimentalProcedureExpanded}
                  expandedLabel="Experimental Procedure"
                  collapsedLabel="Experimental Procedure"
                  isViewPreview={metadata.isViewPreview}
                  {...this.getTrackingProps()}
                />
              )}
              {(!isEmpty(detail.citations) || detail.elnCitation || detail.experiment) && (
                <ToggleButtonComponent
                  className="rx-reactions-table__content-toggle els-btn-xs hide-for-tablet e2e-show-hide-reference"
                  ariaControls={`reaction-references-${index}`}
                  expanded={referencesForcedShow}
                  toggleExpand={this.toggleReferencesForcedShow}
                  expandedLabel={this.isCIDatabase ? 'Hide Reference/Experiment' : 'Hide Reference'}
                  collapsedLabel={this.isCIDatabase ? 'Show Reference/Experiment' : 'Show Reference'}
                  isViewPreview={metadata.isViewPreview}
                />
              )}
            </div>
          </td>

          {/* Yields section */}
          <td className="rx-reactions-table__yield display-table-cell">{this.getYields()}</td>

          {/* Reference/Experiment section - wide screen */}
          <td className="rx-reactions-table__references show-for-tablet display-table-cell" id={`reaction-references-${index}`}>
            <ConditionsReferenceExperimentComponent
              metadata={metadata}
              index={index}
              detail={detail}
              onFullTextLinkClick={onFullTextLinkClick}
              onIssueCitedCounterClick={onIssueCitedCounterClick}
              onAbstractOpen={onAbstractOpen}
              onAuthorClick={onAuthorClick}
            />
          </td>
        </tr>

        {/* Experimental procedure section */}
        {experimentalProcedureExpanded && (
          <tr className={`display-table-row ${isVisible ? '' : 'rx-reactions-table__row-not-visible'}`}>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            {renderCheckboxesColumn && <th className="rx-reactions-table__extra" />}
            <td
              className="rx-reactions-table__procedure display-table-cell"
              colSpan={renderCheckboxesColumn ? 3 : 4}
              id={`reaction-procedure-${index}`}>
              <div className="rx-reactions-table__header">{this.getExampleLabelTitle()}</div>
              <InnerHTML tag="div" className="rx-reactions-table__content" html={detail.experimentalProcedure} extendedTags />
            </td>
          </tr>
        )}

        {/* Reference/Experiment section - toggled on small screen */}
        {referencesForcedShow && (
          <>
            <tr className="rx-reactions-table__header display-table-row">
              <td colSpan="4" className="rx-reactions-table__references display-table-cell hide-for-tablet">
                {this.isCIDatabase ? 'Reference/Experiment' : 'Reference'}
              </td>
            </tr>

            <tr
              className={`rx-reactions-table__item display-table-row hide-for-tablet${isHit ? ' rx-table__group--highlight' : ''}`}
              {...CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem}>
              <td className="rx-reactions-table__references display-table-cell" colSpan="4" id={`reaction-references-${index}`}>
                <ConditionsReferenceExperimentComponent
                  metadata={metadata}
                  index={index}
                  detail={detail}
                  onFullTextLinkClick={onFullTextLinkClick}
                  onIssueCitedCounterClick={onIssueCitedCounterClick}
                  onAbstractOpen={onAbstractOpen}
                  onAuthorClick={onAuthorClick}
                />
              </td>
            </tr>
          </>
        )}

        {/* Additional CI info section */}
        {this.additionalInfoVisible && (
          <tr className={`rx-reactions-table__item display-table-row${isHit ? ' rx-table__group--highlight' : ''}`}>
            <td className="rx-reactions-table__additional-info display-table-cell" colSpan="4">
              <ReactionAdditionalInfoComponent detail={detail} databaseId={databaseId} index={index} reactionId={reactionId} />
            </td>
          </tr>
        )}
      </>
    );
  }
}

ReactionDetailRowComponent.defaultProps = {
  onExperimentalProcedureOpen: () => null,
  onFullTextLinkClick: () => null,
  onIssueCitedCounterClick: () => null,
  onAbstractOpen: () => null,
  onAuthorClick: () => null,
  allowAdditionalInfoShow: true,
  renderCheckboxesColumn: false,
  isVisible: true,
  onRenderedRowsNumberChange: () => null,
};

ReactionDetailRowComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  detail: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  reactionId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  isHit: PropTypes.bool,
  spinnerId: PropTypes.string,
  largestProduct: PropTypes.string,
  onExperimentalProcedureOpen: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
  allowAdditionalInfoShow: PropTypes.bool,
  renderCheckboxesColumn: PropTypes.bool,
  isChecked: PropTypes.bool,
  isVisible: PropTypes.bool,
  setChecked: PropTypes.func,
  onRenderedRowsNumberChange: PropTypes.func,
};
